import { useSyncExternalStore } from 'react';
import { SupabaseTransport, SyncEngine, type SyncTransport } from '@pos/sync';
import { getMeta, oldestPendingOp, pruneCashAndAccounts, pruneOldSales, pruneSettledMovements } from '@pos/local-db';
import { env } from '../lib/env.ts';
import { recordTiming } from '../lib/timings.ts';
import { refreshLicense } from './license.ts';
import { db, syncStore } from '../lib/db.ts';
import { supabase } from '../lib/supabase.ts';

let businessId: string | null = null;
let localUserId: string | null = null;

/** Qué negocio y qué usuario local están activos. Sin eso no se sincroniza nada. */
export function setSyncContext(context: { businessId: string | null; userId: string | null }): void {
  businessId = context.businessId;
  localUserId = context.userId;
}

const base = new SupabaseTransport(supabase);

/**
 * Solo se sincroniza si la sesión del servidor es del mismo usuario que está usando el
 * dispositivo. Evita que, p. ej., un empleado que entró offline arrastre la sesión del dueño.
 */
const transport: SyncTransport = {
  currentUserId: async () => {
    const serverUser = await base.currentUserId();
    return serverUser && serverUser === localUserId ? serverUser : null;
  },
  pushBatch: (ops) => base.pushBatch(ops),
  pull: (...args) => base.pull(...args),
  fetchRows: (...args) => base.fetchRows(...args),
};

export const syncEngine = new SyncEngine({ store: syncStore, transport, businessId: () => businessId });

let debounce: ReturnType<typeof setTimeout> | undefined;

/** Pide sincronizar pronto (agrupa varias escrituras seguidas). */
export function requestSync(delayMs = 800): void {
  clearTimeout(debounce);
  debounce = setTimeout(() => void syncEngine.sync(), delayMs);
}

/** Sincroniza al abrir, al volver la conexión, al volver a la pestaña y cada minuto. */
export function startBackgroundSync(): () => void {
  const run = () => {
    if (navigator.onLine) void syncEngine.sync();
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') run();
  };
  window.addEventListener('online', run);
  document.addEventListener('visibilitychange', onVisible);
  const interval = setInterval(run, 60_000);
  run();
  return () => {
    window.removeEventListener('online', run);
    document.removeEventListener('visibilitychange', onVisible);
    clearInterval(interval);
  };
}

function subscribe(callback: () => void) {
  const unsubscribe = syncEngine.subscribe(callback);
  return () => {
    unsubscribe();
  };
}

export function useSyncStatus() {
  return useSyncExternalStore(subscribe, () => syncEngine.getStatus());
}

// Después de sincronizar bien, como mucho cada 5 minutos, el dispositivo informa su estado
// (pendientes, rechazados, el pendiente más viejo y la versión): el dueño lo ve en Sincronización
// y el superadmin en su panel.
let lastHeartbeat = 0;
async function heartbeat(): Promise<void> {
  if (Date.now() - lastHeartbeat < 5 * 60_000) return;
  const device = await getMeta(db, 'device');
  if (!device) return;
  lastHeartbeat = Date.now();
  const [pending, rejected, oldest] = await Promise.all([
    db.outbox.where('status').equals('pending').count(),
    db.outbox.where('status').equals('rejected').count(),
    oldestPendingOp(db),
  ]);
  await supabase.rpc('device_heartbeat', {
    p_device_id: device.id,
    p_app_version: env.appVersion,
    p_pending: pending,
    p_rejected: rejected,
    p_oldest_pending_at: oldest?.created_at ?? null,
  });
}

// Después de sincronizar, como mucho una vez por hora: se borran los movimientos de stock
// locales que el resumen del servidor ya incluye (el stock no cambia; la base no crece).
let lastPrune = 0;
let lastSyncSeen: number | null = null;

// Si el servidor rechazó algo nuevo, puede ser que la licencia haya cambiado (suspendida o en
// modo consulta): se vuelve a consultar enseguida para que el dispositivo deje de cargar datos
// en lugar de acumular rechazos. Como mucho una vez por minuto.
let lastRejected = 0;
let lastLicenseCheck = 0;
syncEngine.subscribe((status) => {
  if (status.syncing) return;
  const grew = status.rejected > lastRejected;
  lastRejected = status.rejected;
  if (!grew || Date.now() - lastLicenseCheck < 60_000) return;
  lastLicenseCheck = Date.now();
  void getMeta(db, 'device')
    .then((device) => refreshLicense(device?.id ?? null))
    .catch(() => undefined);
});
syncEngine.subscribe((status) => {
  if (status.syncing || status.lastSyncAt === lastSyncSeen) return;
  lastSyncSeen = status.lastSyncAt;
  if (status.lastDurationMs != null) recordTiming('Sincronización completa', status.lastDurationMs);
  void heartbeat().catch(() => undefined);
  if (Date.now() - lastPrune < 60 * 60_000) return;
  lastPrune = Date.now();
  void pruneSettledMovements(db).catch((error) => console.warn('[stock] no se pudo podar movimientos', error));
  // Ventas ya enviadas de más de 60 días: el historial completo queda en el servidor.
  void pruneOldSales(db).catch((error) => console.warn('[ventas] no se pudo podar ventas viejas', error));
  void pruneCashAndAccounts(db).catch((error) => console.warn('[caja] no se pudo podar movimientos viejos', error));
});
